import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { authedRoute } from '../../app/routes-base';

export const backofficeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: 'backoffice',
  component: lazyRouteComponent(() => import('./layout'), 'BackofficeLayout'),
});

const dashboardRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/dashboard-page'), 'DashboardPage'),
});

const floorsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'floors',
  component: lazyRouteComponent(() => import('./pages/floors-page'), 'FloorsPage'),
});

export type MenuTab = 'items' | 'categories' | 'modifiers';
const MENU_TABS: readonly MenuTab[] = ['items', 'categories', 'modifiers'];

export const menuRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'menu',
  validateSearch: (search: Record<string, unknown>): { tab?: MenuTab } =>
    MENU_TABS.includes(search.tab as MenuTab) ? { tab: search.tab as MenuTab } : {},
  component: lazyRouteComponent(() => import('./pages/menu-page'), 'MenuPage'),
});

const qrCodesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'qr',
  component: lazyRouteComponent(() => import('./pages/qr-codes-page'), 'QrCodesPage'),
});

const promotionsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'promotions',
  component: lazyRouteComponent(() => import('./pages/promotions-page'), 'PromotionsPage'),
});

const stationsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'stations',
  component: lazyRouteComponent(() => import('./pages/stations-page'), 'StationsPage'),
});

const staffRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'staff',
  component: lazyRouteComponent(() => import('./pages/staff-page'), 'StaffPage'),
});

const shiftsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'shifts',
  component: lazyRouteComponent(() => import('./pages/shifts-page'), 'ShiftsPage'),
});

const reportsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'reports',
  component: lazyRouteComponent(() => import('./pages/reports-page'), 'ReportsPage'),
});

const branchesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'branches',
  component: lazyRouteComponent(() => import('./pages/branches-page'), 'BranchesPage'),
});

const settingsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'settings',
  component: lazyRouteComponent(() => import('./pages/settings-page'), 'SettingsPage'),
});

// Data & privacy: export, retention, cancellation (ADR-0034)
const dataPrivacyRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'data',
  component: lazyRouteComponent(() => import('./pages/data-privacy-page'), 'DataPrivacyPage'),
});

// Subscription & invoices (ADR-0033)
const subscriptionRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'langganan',
  component: lazyRouteComponent(() => import('./pages/subscription-page'), 'SubscriptionPage'),
});

const subscriptionInvoiceRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'langganan/tagihan/$invoiceId',
  component: lazyRouteComponent(
    () => import('./pages/subscription-invoice-page'),
    'SubscriptionInvoicePage',
  ),
});

const supportAccessRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'support-access',
  component: lazyRouteComponent(() => import('./pages/support-access-page'), 'SupportAccessPage'),
});

const brandingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'branding',
  component: lazyRouteComponent(() => import('./pages/branding-page'), 'BrandingPage'),
});

const approvalsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'approvals',
  validateSearch: (search: Record<string, unknown>): { tab?: 'inbox' | 'history' } =>
    search.tab === 'history' || search.tab === 'inbox' ? { tab: search.tab } : {},
  component: lazyRouteComponent(() => import('./pages/approvals-page'), 'ApprovalsPage'),
});

const budgetsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'budgets',
  component: lazyRouteComponent(() => import('./pages/budgets-page'), 'BudgetsPage'),
});

const approvalPoliciesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'approval-policies',
  component: lazyRouteComponent(
    () => import('./pages/approval-policies-page'),
    'ApprovalPoliciesPage',
  ),
});

const delegationsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'delegations',
  component: lazyRouteComponent(() => import('./pages/delegations-page'), 'DelegationsPage'),
});

const apInvoicesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap',
  component: lazyRouteComponent(() => import('./pages/ap-invoices-page'), 'ApInvoicesPage'),
});

const apInvoiceNewRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/invoices/new',
  component: lazyRouteComponent(() => import('./pages/ap-invoice-page'), 'ApInvoiceNewPage'),
});

const apInvoiceRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/invoices/$invoiceId',
  component: lazyRouteComponent(() => import('./pages/ap-invoice-page'), 'ApInvoiceDetailPage'),
});

const apCreditNotesRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/credit-notes',
  component: lazyRouteComponent(() => import('./pages/ap-credit-notes-page'), 'ApCreditNotesPage'),
});

const apPaymentsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/payments',
  component: lazyRouteComponent(() => import('./pages/ap-payments-page'), 'ApPaymentsPage'),
});

const apAgingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/aging',
  component: lazyRouteComponent(() => import('./pages/ap-aging-page'), 'ApAgingPage'),
});

const apSettingsRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'ap/settings',
  component: lazyRouteComponent(() => import('./pages/ap-settings-page'), 'ApSettingsPage'),
});

const reconciliationRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'reconciliation',
  component: lazyRouteComponent(() => import('./pages/reconciliation-page'), 'ReconciliationPage'),
});

// Accounts (ADR-0031): own sign-in security, and the owner's setup guide.
const accountRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'account',
  component: lazyRouteComponent(() => import('./pages/account-page'), 'AccountPage'),
});

const onboardingRoute = createRoute({
  getParentRoute: () => backofficeRoute,
  path: 'onboarding',
  component: lazyRouteComponent(() => import('./pages/onboarding-page'), 'OnboardingPage'),
});

export const backofficeTree = backofficeRoute.addChildren([
  accountRoute,
  onboardingRoute,
  dashboardRoute,
  floorsRoute,
  menuRoute,
  promotionsRoute,
  qrCodesRoute,
  stationsRoute,
  staffRoute,
  shiftsRoute,
  reportsRoute,
  branchesRoute,
  settingsRoute,
  dataPrivacyRoute,
  subscriptionRoute,
  subscriptionInvoiceRoute,
  supportAccessRoute,
  brandingRoute,
  approvalsRoute,
  budgetsRoute,
  approvalPoliciesRoute,
  delegationsRoute,
  apInvoicesRoute,
  apInvoiceNewRoute,
  apInvoiceRoute,
  apCreditNotesRoute,
  apPaymentsRoute,
  apAgingRoute,
  apSettingsRoute,
  reconciliationRoute,
]);
